/**
 * AcceptInvitePage — landing page for invitation links.
 *
 * Flow:
 *   1. Read `token` from URL.
 *   2. Public lookup GET /api/org/invitations/{token} (no auth).
 *   3a. User is signed in → call POST /api/org/invitations/accept
 *       and redirect to the Knowledge Base on success.
 *   3b. User is NOT signed in → stash the token in sessionStorage,
 *       redirect to /signup; after auth, the page is loaded again
 *       and falls into branch 3a.
 *
 * Error path: token missing / expired / used → show a clear error card.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const API = getBackendBaseUrl();
const STASH_KEY = 'bayenah_pending_invite_token';

async function _readJson(r) {
  try { return await r.clone().json(); } catch { try { return await r.json(); } catch { return null; } }
}
async function _readText(r) { try { return await r.clone().text(); } catch { return r.statusText || `HTTP ${r.status}`; } }

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const tokenFromUrl = params.get('token');

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  // Token resolution: URL > sessionStorage. This lets us survive a
  // signup hop where the user leaves the page to authenticate.
  const token = tokenFromUrl || (typeof window !== 'undefined'
    ? sessionStorage.getItem(STASH_KEY)
    : null);

  // Bootstrap: fetch invitation summary.
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) { setLoading(false); setError('missing'); return; }
      try {
        const r = await fetch(`${API}/api/org/invitations/${encodeURIComponent(token)}`);
        if (!r.ok) {
          if (!cancelled) { setLoading(false); setError('not_found'); }
          return;
        }
        const data = await _readJson(r);
        if (!cancelled) { setInvitation(data); setLoading(false); }
      } catch {
        if (!cancelled) { setLoading(false); setError('network'); }
      }
    }
    run();
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    if (!token || !session?.access_token || accepting) return;
    setAccepting(true);
    try {
      const r = await fetch(`${API}/api/org/invitations/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ token }),
      });
      if (!r.ok) throw new Error(await _readText(r));
      // Success — clear stash + redirect to KB.
      try { sessionStorage.removeItem(STASH_KEY); } catch {}
      navigate('/v2/vault', { replace: true });
    } catch (err) {
      setError('accept_failed');
      setAccepting(false);
      // eslint-disable-next-line no-console
      console.error('[invite] accept failed:', err);
    }
  };

  const goSignUp = () => {
    try { sessionStorage.setItem(STASH_KEY, token); } catch {}
    navigate('/signup', { state: { fromInvite: true } });
  };
  const goSignIn = () => {
    try { sessionStorage.setItem(STASH_KEY, token); } catch {}
    navigate('/', { state: { fromInvite: true } });
  };

  // ── UI ───────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-[#F7F5EF] px-6"
      data-testid="accept-invite-page"
    >
      {loading && (
        <div className="flex items-center gap-2 text-[#666] text-[13.5px]" data-testid="accept-invite-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading invitation…
        </div>
      )}

      {!loading && error && (
        <div
          className="max-w-md w-full border border-[#E5E1D8] rounded-lg bg-white p-8 text-center"
          data-testid="accept-invite-error"
        >
          <h1 className="text-[20px] font-semibold text-[#111111] mb-2">
            Invitation not found
          </h1>
          <p className="text-[13.5px] text-[#666666] mb-6">
            This invitation may have expired or already been used.
            Ask your admin to send a new invitation.
          </p>
          <a
            href="/chat"
            className="inline-flex items-center justify-center rounded-full bg-[#111111] text-white text-[13px] font-medium px-5 py-2 hover:bg-black/85"
            data-testid="accept-invite-home"
          >
            Go to Bayenah
          </a>
        </div>
      )}

      {!loading && !error && invitation && (
        <div
          className="max-w-md w-full border border-[#E5E1D8] rounded-lg bg-white p-8 text-center"
          data-testid="accept-invite-card"
        >
          <div className="text-[#111111] text-[28px] font-semibold tracking-tight font-serif mb-6">
            Bayenah
          </div>
          <p className="text-[13.5px] text-[#666666] mb-1">
            <span data-testid="accept-invite-inviter">{invitation.invited_by_name}</span> has invited you to join
          </p>
          <h1 className="text-[24px] font-semibold text-[#111111] mb-1" data-testid="accept-invite-org">
            {invitation.org_name}
          </h1>
          <p className="text-[13px] text-[#888888] mb-6">
            on Bayenah — Saudi Arabia's legal AI platform
          </p>

          {!user && (
            <>
              <button
                type="button"
                onClick={goSignUp}
                data-testid="accept-invite-signup"
                className="block w-full rounded-full bg-[#111111] text-white text-[13.5px] font-medium px-5 py-2.5 hover:bg-black/85"
              >
                Create account and join
              </button>
              <button
                type="button"
                onClick={goSignIn}
                data-testid="accept-invite-signin"
                className="mt-3 text-[12.5px] text-[#005B9E] hover:underline"
              >
                Already have an account? Sign in
              </button>
            </>
          )}

          {user && (
            <button
              type="button"
              onClick={accept}
              disabled={accepting}
              data-testid="accept-invite-join"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#111111] text-white text-[13.5px] font-medium px-5 py-2.5 hover:bg-black/85 disabled:opacity-50"
            >
              {accepting && <Loader2 className="w-4 h-4 animate-spin" />}
              {accepting ? 'Joining…' : `Join ${invitation.org_name}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
